import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockOpen as UnlockIcon,
  Key as ResetPasswordIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { User, CreateUserRequest, UpdateUserRequest } from '../types/auth';
import { userAPI, authAPI } from '../services/api';
import logo from '../assets/images/logo.png';
import AlertDialog from '../components/AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from '../components/CustomTablePagination';
import { getRoles, Role } from '../services/rbac';
import { extractErrorMessage } from '../utils/errorHandler';
import PermissionButton from '../components/PermissionButton';
import { useAuth } from '../contexts/AuthContext';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { user: currentUser } = useAuth();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tempPasswordDialog, setTempPasswordDialog] = useState<{ open: boolean; password: string; email: string }>({
    open: false,
    password: '',
    email: '',
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role_id: 0,
    is_active: true,
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData] = await Promise.all([
        userAPI.getUsers(),
        getRoles(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        full_name: user.full_name,
        role_id: user.role_id || roles[0]?.id || 0,
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role_id: roles[0]?.id || 0,
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormError('');
  };

  const handleSaveUser = async () => {
    try {
      setFormError('');

      if (editingUser) {
        // Update existing user
        const updateData: UpdateUserRequest = {
          email: formData.email,
          full_name: formData.full_name,
          role_id: formData.role_id,
          is_active: formData.is_active,
        };

        if (formData.password.trim()) {
          updateData.password = formData.password;
        }

        await userAPI.updateUser(editingUser.id, updateData);
        showSuccess('Utilisateur modifié avec succès');
      } else {
        // Create new user
        const createData: CreateUserRequest = {
          email: formData.email,
          password: formData.password || 'temp', // Password not used, will be auto-generated
          full_name: formData.full_name,
          role_id: formData.role_id,
          is_active: formData.is_active,
        };

        const result = await userAPI.createUser(createData);
        // Show temporary password dialog
        if (result && (result as any).temporary_password) {
          setTempPasswordDialog({
            open: true,
            password: (result as any).temporary_password,
            email: formData.email,
          });
        } else {
          showSuccess('Utilisateur créé avec succès');
        }
      }

      handleCloseDialog();
      fetchData();
    } catch (err: any) {
      setFormError(extractErrorMessage(err));
    }
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await userAPI.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
      showSuccess('Utilisateur supprimé avec succès');
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleUnlockUser = async (user: User) => {
    try {
      await authAPI.unlockUser(user.id);
      showSuccess(`L'utilisateur ${user.email} a été déverrouillé`);
      fetchData();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleResetPassword = async (user: User) => {
    try {
      const result = await authAPI.resetUserPassword(user.id);
      setTempPasswordDialog({
        open: true,
        password: result.temporary_password,
        email: user.email,
      });
      fetchData();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const getRoleColor = (roleName: string) => {
    const lower = roleName.toLowerCase();
    if (lower.includes('admin')) return 'error';
    if (lower.includes('manager') || lower.includes('gestionnaire')) return 'secondary';
    if (lower.includes('viewer') || lower.includes('visualisateur')) return 'default';
    return 'primary';
  };

  const getRoleName = (user: User): string => {
    return user.user_role?.name || user.role.toUpperCase();
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Gestion des utilisateurs
        </Typography>
        <PermissionButton
          moduleName="Admin"
          operation="add"
          miniModuleName="Users"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#F44336',
            '&:hover': {
              backgroundColor: '#D32F2F',
            }
          }}
        >
          Ajouter un utilisateur
        </PermissionButton>
      </Box>


      <TableContainer component={Paper} sx={getModuleTableTheme('administration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom complet</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Dernière connexion</TableCell>
              <TableCell>Dernière activité</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleName(user)}
                    color={getRoleColor(getRoleName(user))}
                    variant="filled"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.is_locked ? (
                    <Chip
                      label="Verrouillé"
                      color="error"
                      variant="filled"
                      size="small"
                    />
                  ) : (
                    <Chip
                      label={user.is_active ? 'Actif' : 'Inactif'}
                      color={user.is_active ? 'success' : 'default'}
                      variant="filled"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  {formatDateTime(user.last_login)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  {formatDateTime(user.last_active)}
                </TableCell>
                <TableCell>
                  <PermissionButton
                    moduleName="Admin"
                    operation="edit"
                    miniModuleName="Users"
                    component="IconButton"
                    onClick={() => handleOpenDialog(user)}
                    size="small"
                    sx={{
                      color: '#F44336',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      }
                    }}
                  >
                    <EditIcon />
                  </PermissionButton>
                  {user.is_locked && (
                    <PermissionButton
                      moduleName="Admin"
                      operation="edit"
                      miniModuleName="Users"
                      component="IconButton"
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir déverrouiller l'utilisateur "${user.full_name}" ?`,
                          () => handleUnlockUser(user),
                          {
                            title: 'Déverrouiller l\'utilisateur',
                            confirmText: 'Déverrouiller',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      size="small"
                      sx={{
                        color: '#FF9800',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        }
                      }}
                      title="Déverrouiller l'utilisateur"
                    >
                      <UnlockIcon />
                    </PermissionButton>
                  )}
                  <PermissionButton
                    moduleName="Admin"
                    operation="edit"
                    miniModuleName="Users"
                    component="IconButton"
                    onClick={() => {
                      showConfirm(
                        `Êtes-vous sûr de vouloir réinitialiser le mot de passe de "${user.full_name}" ?`,
                        () => handleResetPassword(user),
                        {
                          title: 'Réinitialiser le mot de passe',
                          confirmText: 'Réinitialiser',
                          cancelText: 'Annuler'
                        }
                      );
                    }}
                    size="small"
                    sx={{
                      color: '#2196F3',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      }
                    }}
                    title="Réinitialiser le mot de passe"
                    disabled={currentUser?.id === user.id}
                  >
                    <ResetPasswordIcon />
                  </PermissionButton>
                  <PermissionButton
                    moduleName="Admin"
                    operation="delete"
                    miniModuleName="Users"
                    component="IconButton"
                    onClick={() => {
                      showConfirm(
                        `Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.full_name}" ?`,
                        () => handleDeleteUser(user),
                        {
                          title: 'Confirmer la suppression',
                          confirmText: 'Supprimer',
                          cancelText: 'Annuler'
                        }
                      );
                    }}
                    color="error"
                    size="small"
                    disabled={currentUser?.id === user.id}
                    title={currentUser?.id === user.id ? 'Vous ne pouvez pas supprimer votre propre compte' : ''}
                  >
                    <DeleteIcon />
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Modifier l\'utilisateur' : 'Créer un nouvel utilisateur'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          {!editingUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Un mot de passe temporaire sera généré automatiquement.
              L'utilisateur devra le changer lors de sa première connexion.
            </Alert>
          )}

          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="filled"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          {editingUser && (
            <TextField
              margin="dense"
              label="Mot de passe (laisser vide pour conserver l'actuel)"
              type="password"
              fullWidth
              variant="filled"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          )}

          <TextField
            margin="dense"
            label="Nom complet"
            fullWidth
            variant="filled"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
          />
          
          <FormControl fullWidth margin="dense" variant="filled">
            <InputLabel>Rôle</InputLabel>
            <Select
              value={formData.role_id}
              label="Rôle"
              onChange={(e) => setFormData({ ...formData, role_id: Number(e.target.value) })}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            }
            label="Actif"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            sx={{ 
              backgroundColor: '#F44336',
              '&:hover': {
                backgroundColor: '#D32F2F',
              }
            }}
          >
            {editingUser ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog
        open={tempPasswordDialog.open}
        onClose={() => setTempPasswordDialog({ open: false, password: '', email: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mot de passe temporaire</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Un mot de passe temporaire a été généré pour <strong>{tempPasswordDialog.email}</strong>.
            L'utilisateur devra le changer lors de sa première connexion.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              variant="filled"
              label="Mot de passe temporaire"
              value={tempPasswordDialog.password}
              InputProps={{
                readOnly: true,
              }}
            />
            <Tooltip title="Copier">
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(tempPasswordDialog.password);
                  showSuccess('Mot de passe copié dans le presse-papiers');
                }}
                sx={{ color: '#F44336' }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTempPasswordDialog({ open: false, password: '', email: '' })}
            variant="contained"
            sx={{
              backgroundColor: '#F44336',
              '&:hover': {
                backgroundColor: '#D32F2F',
              },
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default UserManagement;